import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import axios from 'axios';
import '../../styles/ClientProfileFileSelector.css';

/* ── env / token ── */
const getAmsToken = () => localStorage.getItem('amsToken') || '';

/* ── types ── */
export interface DriveData {
  folderId: string;
  fileIds: string[];
  fileNames: string[];
  folderIds: string[];
}

export interface ClientProfileFileSelectorProps {
  clientId: string;
  onFileIdsSelect: (d: DriveData) => void;
  /** When provided, overrides the banner and row-highlight display to show only these files */
  value?: { fileIds: string[]; fileNames: string[] };
}

interface FileItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  size?: string;
  uploader?: string;
  created?: string;
  lastModified?: string;
  parentFolderId?: string;
  children?: FileItem[];
}

/* ── helpers ── */
const mapFiles = (files: any[], parentId: string): FileItem[] =>
  (files || []).map((f: any) => ({
    id: f.databaseId || f.id || '',
    name: f.name || f.fileName || '',
    type: (f.fileOrFolder === 'Folder' || f.isFolder || f.type === 'folder') ? 'folder' : 'file',
    size: f.size != null ? String(f.size) : undefined,
    uploader: f.creatorName || f.uploader || undefined,
    created: f.createDate || f.created || undefined,
    lastModified: f.changeDate || f.lastModified || undefined,
    parentFolderId: f.parentId || parentId,
    children: (f.fileOrFolder === 'Folder' || f.isFolder) ? [] : undefined,
  }));

const formatSize = (raw?: string): string => {
  if (!raw || raw === '-') return '—';
  const n = parseInt(raw, 10);
  if (isNaN(n) || n === 0) return '—';
  if (n >= 1024 * 1024) return (n / 1_048_576).toFixed(1) + ' MB';
  if (n >= 1024) return (n / 1024).toFixed(1) + ' KB';
  return n + ' B';
};

/* ── file type icon (SVG, no PrimeReact) ── */
const FileIcon: React.FC<{ name: string; type: 'file' | 'folder'; size?: number }> = ({ name, type, size = 14 }) => {
  if (type === 'folder') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
      </svg>
    );
  }
  const ext = name.split('.').pop()?.toLowerCase() || '';
  let color = '#8888a8';
  let path = 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6';
  if (ext === 'pdf')  { color = '#ef4444'; path = 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M9 13h6 M9 17h6 M9 9h1'; }
  if (['jpg','jpeg','png'].includes(ext)) color = '#3b82f6';
  if (['doc','docx'].includes(ext)) color = '#1d4ed8';
  if (['xls','xlsx'].includes(ext)) color = '#16a34a';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
};

/* ── info icon ── */
const InfoIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

/* ════════════════════════════════════════════════
   Component
   ════════════════════════════════════════════════ */
const ClientProfileFileSelector: React.FC<ClientProfileFileSelectorProps> = ({
  clientId,
  onFileIdsSelect,
  value,
}) => {
  const [rootFolder, setRootFolder]       = useState<FileItem | null>(null);
  const [currentFolder, setCurrentFolder] = useState<FileItem | null>(null);
  const [tableData, setTableData]         = useState<FileItem[]>([]);
  const [folderHistory, setFolderHistory] = useState<FileItem[]>([]);
  const [historyIndex, setHistoryIndex]   = useState<number>(-1);
  const [expandedFolders, setExpandedFolders] = useState<Record<string, boolean>>({});
  const [loading, setLoading]             = useState(false);
  const [tableLoading, setTableLoading]   = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileItem[]>([]);
  const [error, setError]                 = useState('');

  /* ── fetch folder from API ── */
  const fetchFolder = useCallback(async (folderId?: string): Promise<{ folder: FileItem; children: FileItem[] }> => {
    const params: Record<string, string> = { insuredId: clientId };
    if (folderId) params.folderId = folderId;
    const res = await axios.get(`${(process.env.REACT_APP_Nowcerts_Base_Url || '')}/api/Insured/GetInsuredFilesList`, {
      params,
      headers: { Authorization: `Bearer ${getAmsToken()}` },
    });
    const data = res.data?.data ?? res.data ?? {};
    const id       = data.currentFolderId || folderId || clientId;
    const name     = data.name || (folderId ? 'Folder' : 'Root');
    const children = mapFiles(data.files || [], id);
    return { folder: { id, name, type: 'folder', children }, children };
  }, [clientId]);

  /* ── open a folder into the file table + update history ── */
  const openFolder = useCallback((folder: FileItem, children: FileItem[]) => {
    const resolved: FileItem = { ...folder, children };
    setCurrentFolder(resolved);
    setTableData(children);
    setExpandedFolders(prev => ({ ...prev, [folder.id]: true }));

    setFolderHistory(prev => {
      const base = historyIndex >= 0 ? prev.slice(0, historyIndex + 1) : [];
      const existing = base.findIndex(f => f.id === folder.id);
      if (existing !== -1) {
        setHistoryIndex(existing);
        return base;
      }
      const next = [...base, resolved];
      setHistoryIndex(next.length - 1);
      return next;
    });
  }, [historyIndex]);

  /* ── initial load ── */
  useEffect(() => {
    setRootFolder(null);
    setCurrentFolder(null);
    setTableData([]);
    setFolderHistory([]);
    setHistoryIndex(-1);
    setSelectedFiles([]);
    setExpandedFolders({});
    setError('');
    if (!clientId) return;

    setLoading(true);
    fetchFolder()
      .then(({ folder, children }) => {
        setRootFolder(folder);
        openFolder(folder, children);
      })
      .catch(() => setError('Failed to load client files.'))
      .finally(() => setLoading(false));
  }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── click on folder row / tree node ── */
  const handleFolderOpen = useCallback((item: FileItem) => {
    if (item.children && item.children.length > 0) {
      openFolder(item, item.children);
      return;
    }
    setTableLoading(true);
    fetchFolder(item.id)
      .then(({ folder, children }) => {
        const updated: FileItem = { ...folder, children };
        /* update root tree so the node gets children cached */
        setRootFolder(prev => {
          if (!prev) return prev;
          const patch = (node: FileItem): FileItem => {
            if (node.id === item.id) return updated;
            if (node.children) return { ...node, children: node.children.map(patch) };
            return node;
          };
          return patch(prev);
        });
        openFolder(updated, children);
      })
      .catch(() => {})
      .finally(() => setTableLoading(false));
  }, [fetchFolder, openFolder]);

  /* Display-only list: when caller controls the selection, show only those files */
  const displayFiles: { id: string; name: string }[] = value
    ? value.fileIds.map((id, i) => ({ id, name: value.fileNames[i] || '' }))
    : selectedFiles;

  const isPdfFile = (name: string) => name.toLowerCase().endsWith('.pdf');

  /* ── click on file row — toggle multi-select ── */
  const handleFileSelect = useCallback((item: FileItem) => {
    if (!isPdfFile(item.name)) return;
    setSelectedFiles(prev => {
      const alreadySelected = prev.some(f => f.id === item.id);
      const next = alreadySelected ? prev.filter(f => f.id !== item.id) : [...prev, item];
      const folderId = currentFolder?.id || rootFolder?.id || clientId;
      if (next.length === 0) {
        onFileIdsSelect({ folderId: '', fileIds: [], fileNames: [], folderIds: [] });
      } else {
        onFileIdsSelect({
          folderId,
          fileIds: next.map(f => f.id),
          fileNames: next.map(f => f.name),
          folderIds: next.map(f => f.parentFolderId || currentFolder?.id || ''),
        });
      }
      return next;
    });
  }, [currentFolder, rootFolder, clientId, onFileIdsSelect]);

  const clearSelection = useCallback(() => {
    setSelectedFiles([]);
    onFileIdsSelect({ folderId: '', fileIds: [], fileNames: [], folderIds: [] });
  }, [onFileIdsSelect]);

  /* ── history nav ── */
  const canBack    = historyIndex > 0;
  const canForward = historyIndex >= 0 && historyIndex < folderHistory.length - 1;

  const goBack = () => {
    if (!canBack) return;
    const idx    = historyIndex - 1;
    const folder = folderHistory[idx];
    setHistoryIndex(idx);
    setCurrentFolder(folder);
    setTableData(folder.children || []);
  };

  const goForward = () => {
    if (!canForward) return;
    const idx    = historyIndex + 1;
    const folder = folderHistory[idx];
    setHistoryIndex(idx);
    setCurrentFolder(folder);
    setTableData(folder.children || []);
  };

  /* ── breadcrumb path ── */
  const getBreadcrumb = (): FileItem[] => {
    if (!rootFolder) return [];
    const target = currentFolder?.id || rootFolder.id;
    const path: FileItem[] = [];
    const dfs = (node: FileItem, acc: FileItem[]): boolean => {
      const next = [...acc, node];
      if (node.id === target) { path.push(...next); return true; }
      for (const child of node.children || []) { if (dfs(child, next)) return true; }
      return false;
    };
    dfs(rootFolder, []);
    return path.length ? path : [rootFolder];
  };
  const breadcrumb = getBreadcrumb();

  /* ── tree node renderer ── */
  const renderTree = (node: FileItem, depth = 0): React.ReactNode => {
    if (node.type !== 'folder') return null;
    const isOpen   = !!expandedFolders[node.id];
    const isActive = currentFolder?.id === node.id;
    const indent   = depth * 14;
    return (
      <div key={node.id}>
        <div
          className={`cpfs-tree__node${isActive ? ' cpfs-tree__node--active' : ''}`}
          style={{ paddingLeft: 12 + indent }}
          onClick={() => handleFolderOpen(node)}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter') handleFolderOpen(node); }}
        >
          <span className={`cpfs-tree__chevron${isOpen ? ' cpfs-tree__chevron--open' : ''}`}>
            <FiChevronRight size={11} />
          </span>
          <FileIcon name={node.name} type="folder" size={13} />
          <span className="cpfs-tree__label" title={node.name}>{node.name}</span>
        </div>
        {isOpen && (node.children || []).filter(c => c.type === 'folder').map(c => renderTree(c, depth + 1))}
      </div>
    );
  };

  /* ════════ render ════════ */
  if (loading) {
    return (
      <div className="cpfs-empty" style={{ minHeight: 280, gap: 'var(--space-4)' }}>
        <div className="qp-history-loader__orb" style={{ width: 56, height: 56 }}>
          <div className="qp-history-loader__ring qp-history-loader__ring--1" />
          <div className="qp-history-loader__ring qp-history-loader__ring--2" />
          <div className="qp-history-loader__core" style={{ inset: 18 }}>
            <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <p className="qp-history-loader__title" style={{ fontSize: 'var(--text-sm)' }}>Loading Files</p>
          <p className="qp-history-loader__sub">
            Fetching client profile
            <span className="qp-init-loader__dots"><span>.</span><span>.</span><span>.</span></span>
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cpfs-empty">
        <InfoIcon size={22} />
        <p className="cpfs-empty__text">{error}</p>
      </div>
    );
  }

  return (
    <div className="cpfs">
      {/* ── Selected files banner ── */}
      {displayFiles.length > 0 && (
        <motion.div
          className="cpfs-banner"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.16 }}
        >
          <div className="cpfs-banner__inner" style={{ flexWrap: 'wrap', gap: 6 }}>
            <span className="cpfs-banner__label">{displayFiles.length === 1 ? 'Selected' : `Selected (${displayFiles.length})`}</span>
            {displayFiles.map(f => (
              <span key={f.id} className="cpfs-banner__chip">
                <FileIcon name={f.name} type="file" size={12} />
                <span className="cpfs-banner__chip-name" title={f.name}>{f.name}</span>
                {!value && (
                  <button className="cpfs-banner__chip-x" onClick={() => handleFileSelect(f as FileItem)} title="Remove">
                    <FiX size={11} />
                  </button>
                )}
              </span>
            ))}
            {!value && displayFiles.length > 1 && (
              <button className="cpfs-banner__chip-x" style={{ marginLeft: 4 }} onClick={clearSelection} title="Clear all">
                Clear all
              </button>
            )}
          </div>
        </motion.div>
      )}

      {/* ── Path bar ── */}
      <div className="cpfs-pathbar">
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button className={`cpfs-pathbar__btn${!canBack ? ' cpfs-pathbar__btn--disabled' : ''}`}
            onClick={goBack} disabled={!canBack} title="Back">
            <FiChevronLeft size={12} />
          </button>
          <button className={`cpfs-pathbar__btn${!canForward ? ' cpfs-pathbar__btn--disabled' : ''}`}
            onClick={goForward} disabled={!canForward} title="Forward">
            <FiChevronRight size={12} />
          </button>
        </div>
        <div className="cpfs-pathbar__crumbs">
          {breadcrumb.map((seg, i) => (
            <React.Fragment key={seg.id}>
              {i > 0 && <span className="cpfs-pathbar__sep">/</span>}
              <span
                className={`cpfs-pathbar__crumb${i === breadcrumb.length - 1 ? ' cpfs-pathbar__crumb--active' : ''}`}
                title={seg.name}
                onClick={() => i < breadcrumb.length - 1 && handleFolderOpen(seg)}
              >
                {seg.name}
              </span>
            </React.Fragment>
          ))}
        </div>
        {tableLoading && <div className="qp-spinner" style={{ width: 13, height: 13, flexShrink: 0 }} />}
      </div>

      {/* ── Split panel: tree + table ── */}
      <div className="cpfs-browser">
        {/* Left: folder tree */}
        <div className="cpfs-tree">
          {rootFolder && renderTree(rootFolder)}
        </div>

        {/* Right: file table */}
        <div className="cpfs-table-wrap">
          {tableLoading ? (
            <div className="cpfs-empty" style={{ minHeight: 200 }}>
              <div className="qp-spinner" />
              <p className="cpfs-empty__text">Loading…</p>
            </div>
          ) : (
            <table className="cpfs-table">
              <thead>
                <tr>
                  <th className="cpfs-table__th">Name</th>
                  <th className="cpfs-table__th">Type</th>
                  <th className="cpfs-table__th">Size</th>
                  <th className="cpfs-table__th">Uploaded by</th>
                  <th className="cpfs-table__th">Created</th>
                  <th className="cpfs-table__th">Modified</th>
                </tr>
              </thead>
              <tbody>
                {(!tableData || tableData.length === 0) && (
                  <tr>
                    <td colSpan={6} className="cpfs-table__empty">This folder is empty</td>
                  </tr>
                )}
                {tableData.map(item => (
                  <tr
                    key={item.id}
                    className={[
                      'cpfs-table__row',
                      displayFiles.some(f => f.id === item.id) ? 'cpfs-table__row--selected' : '',
                      item.type === 'folder' ? 'cpfs-table__row--folder' : '',
                      item.type === 'file' && !isPdfFile(item.name) ? 'cpfs-table__row--disabled' : '',
                    ].filter(Boolean).join(' ')}
                    onClick={() => item.type === 'folder' ? handleFolderOpen(item) : handleFileSelect(item)}
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); item.type === 'folder' ? handleFolderOpen(item) : handleFileSelect(item); } }}
                  >
                    <td className="cpfs-table__td cpfs-table__td--title">
                      <span className="cpfs-table__icon"><FileIcon name={item.name} type={item.type} size={14} /></span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>{item.name}</span>
                      {item.type === 'folder' && <FiChevronRight size={11} style={{ marginLeft: 'auto', opacity: 0.4, flexShrink: 0 }} />}
                    </td>
                    <td className="cpfs-table__td">{item.type === 'folder' ? 'Folder' : 'File'}</td>
                    <td className="cpfs-table__td">{formatSize(item.size)}</td>
                    <td className="cpfs-table__td">{item.uploader || '—'}</td>
                    <td className="cpfs-table__td">{item.created || '—'}</td>
                    <td className="cpfs-table__td">{item.lastModified || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ClientProfileFileSelector);

